import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ChemicalsService } from '../../services/chemicals.service';
import { ChemicalListItemDto } from '@models/chemical-list-item-dto.interface';
import { EditChemicalComponent, EditChemicalDialogData } from './edit-chemical/edit-chemical.component';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '@shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'app-chemicals',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
    ],
    templateUrl: './chemicals.component.html',
    styleUrl: './chemicals.component.scss'
})
export class ChemicalsComponent implements OnInit {
    private readonly chemicalsService = inject(ChemicalsService);
    private readonly dialog = inject(MatDialog);
    private readonly cdr = inject(ChangeDetectorRef);

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        if (sort) { this.dataSource.sort = sort; }
    }

    @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
        if (paginator) { this.dataSource.paginator = paginator; }
    }

    dataSource = new MatTableDataSource<ChemicalListItemDto>([]);
    displayedColumns: string[] = ['number','edit', 'delete', 'name', 'materialType', 'materialSubType', 'specificGravity', 'density', 'mixingTime'];
    isLoading = false;

    ngOnInit(): void {
        this.dataSource.filterPredicate = (chemical, filter) =>
            chemical.name?.toLowerCase().includes(filter) ?? false;
        this.loadChemicals();
    }

    applyFilter(searchTerm: string): void {
        this.dataSource.filter = searchTerm.trim().toLowerCase();
        this.dataSource.paginator?.firstPage();
    }

    onCreate(): void {
        const dialogRef = this.dialog.open<EditChemicalComponent, EditChemicalDialogData, boolean>(
            EditChemicalComponent,
            {
                data: { mode: 'create' },
                width: '540px',
                disableClose: true,
            }
        );
        dialogRef.afterClosed().subscribe(saved => {
            if (saved) this.loadChemicals();
        });
    }

    onEdit(chemical: ChemicalListItemDto): void {
        const dialogRef = this.dialog.open<EditChemicalComponent, EditChemicalDialogData, boolean>(
            EditChemicalComponent,
            {
                data: { chemicalId: chemical.id, mode: 'edit' },
                width: '540px',
                disableClose: true,
            }
        );
        dialogRef.afterClosed().subscribe(saved => {
            if (saved) this.loadChemicals();
        });
    }

    onDelete(chemical: ChemicalListItemDto): void {
        const dialogRef = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
            ConfirmationDialogComponent,
            {
                data: {
                    title: 'Delete Material',
                    primaryText: `Are you sure you want to delete ${chemical.name}?`,
                    confirmText: 'Delete',
                },
                width: '420px',
                disableClose: true,
            }
        );
        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) this.deleteChemical(chemical.id);
        });
    }

    private deleteChemical(chemicalId: number): void {
        this.chemicalsService.delete(chemicalId).subscribe({
            next: () => this.loadChemicals(),
        });
    }

    private loadChemicals(): void {
        this.isLoading = true;
        this.chemicalsService.getAll().subscribe({
            next: (chemicals) => {
                this.dataSource.data = chemicals;
                this.isLoading = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.isLoading = false;
                this.cdr.markForCheck();
            }
        });
    }
}
